import { Injectable } from '@angular/core'
import {
  NgModalServiceInterface,
  ModalResponse,
} from '../../models/modal.model'
import { NgNotificationServiceInterface } from '../../models/notification.model'
import { Supplier } from './models/supplier.entity'
import {
  parseSupplierModal,
  parseSupplierNotification,
  SupplierModalCode,
  SupplierNotificationCode,
} from './libs/supplier-notification.lib'
import { SupplierSearchParams } from './models/supplier-search.model'
import {
  from,
  last,
  Observable,
  scan,
  concatMap,
  switchMap,
  map,
  combineLatest,
  of,
} from 'rxjs'
import { SuppliersService } from './suppliers.service'
import { CountriesService } from '../countries'
import { uniq, uniqBy } from 'lodash'
import {
  SuppliersListingData,
  SuppliersListingPage,
} from './models/supplier-listing.model'

@Injectable({
  providedIn: 'root',
})
export class SuppliersRepository {
  constructor(
    private countriesService: CountriesService,
    private suppliersService: SuppliersService,
    private ngModalService?: NgModalServiceInterface,
    private ngNotificationService?: NgNotificationServiceInterface,
  ) {}

  /**
   * Search suppliers and external data
   * @param searchParams - the search params
   * @param fields - the fields
   * @param listingData - the already loaded listing data
   * @returns the observable for search suppliers
   */
  searchSuppliers$(
    searchParams: SupplierSearchParams,
    returnAll?: boolean,
  ): Observable<SuppliersListingPage> {
    const needsPagination =
      searchParams.limit !== undefined &&
      searchParams.limit > this.suppliersService.limit

    return needsPagination
      ? this._paginateSuppliers$(searchParams)
      : this._searchSuppliers$(searchParams, returnAll)
  }

  /**
   * Search suppliers with relative external data
   * @param searchParams - the search params
   * @param fields - the suppliers fields
   * @param listingData - the already loaded listing data
   * @returns the observable<SuppliersListingPage> for search suppliers
   */
  private _searchSuppliers$(
    searchParams: SupplierSearchParams,
    returnAll?: boolean,
  ): Observable<SuppliersListingPage> {
    return this.suppliersService
      .search$(searchParams, returnAll)
      .pipe(
        switchMap((page) =>
          this._loadExtData$(page).pipe(
            map((extData) => ({ ...page, extData: extData.countries })),
          ),
        ),
      )
  }

  /**
   * Load external page goods-returns data
   * @param page - the goods-returns page
   * @param fields - the goods-returns fields
   * @returns the observable for load external data
   */
  private _loadExtData$(
    page: SuppliersListingPage,
  ): Observable<SuppliersListingData> {
    const countryCodes: string[] = uniq(
      (page.data ?? [])
        .filter((s) => s.countryCode !== undefined)
        .map((s) => s.countryCode),
    )

    const obs$ = {
      countries: countryCodes.length
        ? this.countriesService.store$(countryCodes, [])
        : of([]),
    }

    return combineLatest(obs$).pipe(
      map((results) => ({
        countries: results.countries ?? [],
      })),
    )
  }

  /**
   * Search more than 200 suppliers with relative external data
   * @param searchParams - the search params
   * @param fields - the suppliers fields
   * @param listingData - the already loaded listing data
   * @returns the observable<SuppliersListingPage> for search suppliers
   */
  private _paginateSuppliers$(
    searchParams: SupplierSearchParams,
  ): Observable<SuppliersListingPage> {
    const totalCount = searchParams.limit || this.suppliersService.limit
    const pagesCount = Math.ceil(totalCount / this.suppliersService.limit)

    return from(
      Array.from({ length: pagesCount }, (_, i) => ({
        ...searchParams,
        limit: this.suppliersService.limit,
        offset: i * this.suppliersService.limit,
      })),
    ).pipe(
      concatMap((pageParams: SupplierSearchParams) =>
        this._searchSuppliers$(pageParams),
      ),
      scan<SuppliersListingPage, SuppliersListingPage>(
        (acc, current) => ({
          totalCount: uniqBy(
            [...(acc.data || []), ...(current.data || [])],
            '_id',
          ).length,
          data: uniqBy([...(acc.data || []), ...(current.data || [])], '_id'),
          extData: acc.extData,
        }),
        { totalCount: 0, data: [] },
      ),
      last(),
    )
  }

  notify(options: { code?: SupplierNotificationCode; message?: string }): void {
    this.ngNotificationService?.show(
      options.message
        ? { title: 'Error', message: options.message, color: 'error' }
        : parseSupplierNotification(options.code),
    )
  }

  async alert(
    code: SupplierModalCode,
    supplier?: Supplier,
  ): Promise<ModalResponse> {
    const settings = parseSupplierModal(code, supplier)
    if (!this.ngModalService || !settings) {
      return 'closed'
    }
    return await this.ngModalService.showDialog(settings)
  }
}
